/**
 * New view (screenshots/17, section 2): a saved filter on an existing tab, shown under YOUR VIEWS
 * (personal) or TEAM VIEWS (owners/admins of team accounts can publish to the whole team).
 * The "Preview: N meetings match" line needs the follows/matching engine (backend ticket); it shows
 * once that endpoint exists.
 */
import { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronDown, Landmark, Lock, Plus, ScrollText, Tag, UserRound, Users, X } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { navStore } from "./navStore";
import type { CustomView, TabId } from "./navConfig";
import { toastStore } from "../stores/toastStore";
import { useFocusTrap } from "../hooks/useFocusTrap";

type FilterType = CustomView["badge"]["type"];
interface Filter { type: FilterType; value: string }

const START: { id: TabId; label: string; icon: typeof CalendarDays }[] = [
  { id: "meetings", label: "Meetings", icon: CalendarDays },
  { id: "legislation", label: "Legislation", icon: ScrollText },
  { id: "people", label: "People", icon: Users },
];
const TYPE_ICON: Record<FilterType, typeof Tag> = { legislation: ScrollText, person: UserRound, topic: Tag, committee: Landmark };
const FILTER_KEY: Record<FilterType, string> = { legislation: "record", person: "person", topic: "topic", committee: "body" };

export function NewViewDialog({ canPublishTeamViews, orgId, onClose }: { canPublishTeamViews: boolean; orgId: string | null; onClose: () => void }) {
  const [name, setName] = useState("");
  const [tab, setTab] = useState<TabId>("meetings");
  const [filters, setFilters] = useState<Filter[]>([]);
  const [adding, setAdding] = useState<{ type: FilterType; value: string } | null>(null);
  const [showCount, setShowCount] = useState(true);
  const [scope, setScope] = useState<"personal" | "team">("personal");
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box);

  useEffect(() => {
    nameRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);

  const create = async () => {
    if (!name.trim()) { nameRef.current?.focus(); return; }
    const view: CustomView = {
      id: crypto.randomUUID(),
      label: name.trim(),
      tab,
      filters: filters.reduce<Record<string, string[]>>((acc, f) => ({ ...acc, [FILTER_KEY[f.type]]: [...(acc[FILTER_KEY[f.type]] ?? []), f.value] }), {}),
      badge: { type: filters[0]?.type ?? "topic" },
      showCount,
      scope,
    };
    if (scope === "team" && orgId) {
      setSaving(true);
      const res = await fetch(`/api/orgs/${encodeURIComponent(orgId)}/views`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(view) });
      setSaving(false);
      if (!res.ok) { toastStore.getState().show({ message: "Couldn't publish the team view. Try again." }); return; }
      toastStore.getState().show({ message: `Team view “${view.label}” added` });
      dispatchEvent(new Event("dashboard:teamviews"));
    } else {
      navStore.getState().addView(view);
    }
    onClose();
  };

  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="new-view-title" ref={box}>
        <header className="dialog-head">
          <h2 id="new-view-title">New view</h2>
          <button className="icon-btn" aria-label="Close" onClick={onClose}><X size={16} /></button>
        </header>

        <label className="field-label" htmlFor="view-name">Name</label>
        <input id="view-name" ref={nameRef} className="text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ward 12 renters" />

        <span className="field-label">Start from</span>
        <div className="segmented" role="radiogroup" aria-label="Start from">
          {START.map(({ id, label, icon: I }) => (
            <button key={id} role="radio" aria-checked={tab === id} className="segment" onClick={() => setTab(id)}><I size={13} />{label}</button>
          ))}
        </div>

        <span className="field-label">Filters</span>
        <div className="filter-row">
          {filters.map((f, i) => {
            const I = TYPE_ICON[f.type];
            return (
              <span key={i} className="filter-chip" data-type={f.type}>
                <I size={12} />{f.value}
                <button aria-label={`Remove ${f.value}`} onClick={() => setFilters(filters.filter((_, k) => k !== i))}><X size={11} /></button>
              </span>
            );
          })}
          {adding ? (
            <form className="filter-add" onSubmit={(e) => { e.preventDefault(); if (adding.value.trim()) setFilters([...filters, { type: adding.type, value: adding.value.trim() }]); setAdding(null); }}>
              <Dropdown className="filter-add__type" label="Filter type" size="sm" value={adding.type} onChange={(type) => setAdding({ ...adding, type })} chevron={<ChevronDown size={12} aria-hidden />}
                options={[{ value: "legislation" as FilterType, label: "Legislation" }, { value: "person" as FilterType, label: "Person" }, { value: "topic" as FilterType, label: "Topic" }, { value: "committee" as FilterType, label: "Committee" }]} />
              <input aria-label="Filter value" autoFocus value={adding.value} onChange={(e) => setAdding({ ...adding, value: e.target.value })} />
            </form>
          ) : (
            <button className="link-add" onClick={() => setAdding({ type: "topic", value: "" })}><Plus size={12} />Add filter</button>
          )}
        </div>

        <div className="dialog-switch">
          <span>Show a count of new items in the sidebar</span>
          <button role="switch" aria-checked={showCount} aria-label="Show a count of new items in the sidebar" className="switch" onClick={() => setShowCount(!showCount)} />
        </div>
        <div className="dialog-switch">
          <span>Visible to</span>
          <Dropdown className="select-pill" label="Visible to" size="sm" align="end" value={scope} onChange={setScope} icon={<Lock size={12} aria-hidden />} chevron={<ChevronDown size={12} aria-hidden />}
            options={[{ value: "personal" as const, label: "Only you" }, ...(canPublishTeamViews ? [{ value: "team" as const, label: "Whole team" }] : [])]} />
        </div>

        <footer className="dialog-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={saving} onClick={create}><Plus size={14} />Create view</button>
        </footer>
      </div>
    </div>
  );
}
